import { Injectable } from '@angular/core';
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Cookie } from 'ng2-cookies';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import { Observable } from "rxjs/Observable";
import cloneDeep from 'lodash/cloneDeep';

// Import de nm-socle-application
import { ConfigService } from './../../services/config.service';
import { SecurityService } from './../../services/security.service';
import { CryptoService } from './../../services/crypto.service';
import { GeoServices } from './../../services/geo.service';
import { TranslateService } from 'ng2-translate';

@Injectable()
export class LoginService {
    public loggedInUser = new BehaviorSubject(null);
    public reloggedInUser = new BehaviorSubject(null);
    public loginError: string = null;
    /**
     * Creates an instance of LoginService.
     *
     * @param {ConfigService} configService Service stockant toutes les valeurs de configurations applicatives
     * @param {Http} http Service Ajax
     * @param {SecurityService} SecurityService Service stockant les paramètres de sécurité
     * @param {GeoServices} geoServices Service d'interaction avec le back
     * @param {MapConfigService} mapConfigService gestion de la configuration de la carte
     *
     * @memberOf BootstrapService
     */
    constructor(
        private _geoServices: GeoServices,
        private _configService: ConfigService,
        private _securtiyService: SecurityService,
        private _cryptoService: CryptoService,
        private _translateService: TranslateService,
    ) {
    }

    /**
     * Réalise l'authentification côté GeoServices et récupére un token de
     * connexion utilisé pour les prochaines requetes utilisant le WSSE
     *
     * @private
     * @returns Observable<Response>
     * @memberof BootstrapService
     */
    public login(login?: string, psw?: string, customerId?: number, reLogin?: boolean):Observable<any> {

        this.loginError = null;

        if (login !== undefined) {
            this._configService.put('login', login);
        }

        if (customerId !== undefined) {
            this._configService.put('customerId', customerId);
        }

        Cookie.delete('timeout', '/', window.location.hostname);

        return this.getUserSalt().flatMap((salt, index) => {
            // Si le user a un salt, alors la configuration de la sécurité est la nouvelle
            salt = (typeof salt !== "string" ? undefined : salt);
            if (psw !== undefined) {
                this.setPassword(psw, salt);
            }

            return this._geoServices.doRequestUriKey('common_login_uri')
                .map((res) => this._geoServices.extractData(res))
                .map((res, index) => {
                    Cookie.set('token', this._securtiyService.generateNmToken(), undefined, '/', window.location.hostname);

                    this.setConfigUser(res);

                    if (reLogin)
                        this.reloggedInUser.next(res);
                    else
                        this.loggedInUser.next(res);

                    return res;
                })
                .catch(error => this._geoServices.handleError(error, false))
                .catch(errorMessage => {
                    this.loginError = errorMessage;
                    return Observable.throw(errorMessage);
                })
        });
    }

    public setPassword(psw: string, salt: string=undefined) {
        let userEncodingConfig = this.getEncodingConfig(salt);
        this._configService.put('pwd', this._cryptoService.encode(psw, userEncodingConfig, salt));
    }

    /**
     * Get the encoding configuration
     * - with salt : user encoding configuration
     * - without salt : leagacy user encoding configuration
     * @param salt
     */
    private getEncodingConfig(salt){
        let userEncodingConfig = this._securtiyService.get('legacyuser');
        if (salt !== undefined) {
            userEncodingConfig = this._securtiyService.get('user');
        }
        return userEncodingConfig;
    }

    /**
     * Get the encrypted password even when the password is required not to be hashed by front
     */
    public getPasswordEncrypted(){
        return this.getUserSalt().map(salt => {
            salt = (typeof salt !== "string" ? undefined : salt);
            let userEncodingConfig = this.getEncodingConfig(salt);
            if ( userEncodingConfig.nb_iteration == 0 ){
                // In case the number of iterations is 0, the password is not hashed, which makes it impossible to
                //   connect to nm-administration services. We need to encrypt password with some hashing interations.
                // First recover the password in its initial state (as entered by user)
                let psw = this._cryptoService.decode( this._configService.get('pwd'), userEncodingConfig );
                let userEncodingConfigDB = cloneDeep(userEncodingConfig);
                // Encrypt with the same number of iterations as on server side
                userEncodingConfigDB.nb_iteration = userEncodingConfigDB.nb_iteration_server;
                return this._cryptoService.encode( psw, userEncodingConfigDB, salt );
            }
            else{
                return this._configService.get('pwd');
            }
        });
    }

    public setConfigUser(res: any) { // res = user object
        this._configService.put('login', res['user_login']); 
        this._configService.put('userId', res['user_id']);
        this._configService.put('userGroupId', res['user_group_id']);
        this._configService.put('userGeoLim', res['geolim']);
    }

    private getUserSalt(): Observable<any> {
        // TODO : pas de header de WSSE
        let requestParams = {
            '{login}': encodeURIComponent(this._configService.get('login'))
        };

        let uri = this._configService.get('common_user_get_salt_url').value;

        return this._geoServices.doRequest(uri, 'GET', [], requestParams, undefined, undefined, false)
            .map((res) => this._geoServices.extractData(res))
            .catch(error => this._geoServices.handleError(error)).share();
    }

    public sendLostPasswordRequest(email: string, login:string):Observable<string> {
        let requestParams = {
            '{email}': email,
            '{login}': login,
        };

        return this._geoServices.doRequestUriKey('common_user_lost_password_request_url', 'GET', undefined, requestParams)
            .map((res) => this._geoServices.extractData(res))
            .catch(error => this._geoServices.handleError(error, false));
    }

    /**
     * Set a new password, from a temporary token for password recovery
     * @param recoveryToken : token from password recovery
     * @param userObj : description of a user, should contain a passw and a passwConfirm field
     */
    public recoverPassword(recoveryToken: string, userObj: any) {

        let requestParams = {
            '{token}': recoveryToken
        };

        return this._geoServices.doRequestUriKey(
            'common_user_recover_lost_password_request_url',
            'PUT',
            undefined,
            requestParams,
            userObj,
            undefined,
            '',
            ''
        )
            .map((res) => this._geoServices.extractData(res))
            .catch(error => this._geoServices.handleError(error));

    }

    /**
     * Recover user information from password recovery token
     * @param recoveryToken : password recovery token
     */
    public recoverUser(recoveryToken: string) {

        let requestParams = {
            '{token}': recoveryToken
        };

        return this._geoServices.doRequestUriKey(
            'common_user_recover_request_url',
            'GET',
            undefined,
            requestParams,
            undefined,
            undefined,
            '',
            ''
        )
        .map((res) => this._geoServices.extractData(res))
        .catch(error => this._geoServices.handleError(error));

    }

    public logout() {
        this._configService.put('userId', null);
        this._configService.put('customerId', null);
        this._configService.put('login', null);

        this.deleteUserSecurity();

        this.loggedInUser.next(null);
    }

    public deleteUserSecurity() {
        this._configService.put('pwd', null);
        Cookie.delete('token', '/', window.location.hostname);

        this.reloggedInUser.next(null);
    }

    public isUserLogged():boolean {
        let login:string = this._configService.get('login');
        let pwd:string = this._configService.get('pwd');

        return login != null && login != undefined && pwd != null && pwd != undefined;
    }

    public getCookieTimeOut() {
        return Cookie.get('timeout');
    }

    public askDemo(email:string, nom:string, prenom:string, mailFrom:string, destMail:string, url:string, login:string, passwd:string, signature):Observable<boolean> {
        let datas = {
            "email":email,
            "nom":nom,
            "prenom":prenom,
            "mailFrom":mailFrom,
            "destMail":destMail,
            "url":url,
            "login":login,
            "passwd":passwd,
            "signature":signature,
        }
        return this._geoServices.doRequestUriKey('common_ask_demo_uri', 'POST', undefined, undefined, datas, undefined, this._translateService.instant('ask-demo.notification.theme'), this._translateService.instant('ask-demo.notification.message'))
            .map((res) => this._geoServices.extractData(res))
            .map((res, index) => {
                return res == true;
            })
            .catch(error => this._geoServices.handleError(error, false))
            .catch(errorMessage => {
                return Observable.of(false);
            })
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/components/login/login.service.ts